import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Printer, Loader2, Palette, Plus, Trash2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Header } from '../components/Header';
import { OnePagerPreview } from '../components/OnePagerPreview';
import { PreviewScaler } from '../components/PreviewScaler';
import { colorSchemes, printSizes } from '../data/catalog';
import { printPreview } from '../lib/print';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printSize, setPrintSize] = useState('letter');

  useEffect(() => {
    const load = async () => {
      try { const res = await api.get(`/onepagers/${id}`); setDoc(res.data); }
      catch { toast({ title: 'Not found', variant: 'destructive' }); navigate('/library'); }
      finally { setLoading(false); }
    };
    load();
  }, [id, navigate, toast]);

  const update = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const updateContent = (patch) => setDoc((d) => ({ ...d, content: { ...d.content, ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/onepagers/${id}`, doc);
      toast({ title: 'Saved!', description: 'Changes saved to your library.' });
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const setKeyPoint = (i, val) => { const kp = [...(doc.content.keyPoints || [])]; kp[i] = val; updateContent({ keyPoints: kp }); };
  const addKeyPoint = () => updateContent({ keyPoints: [...(doc.content.keyPoints || []), 'New point'] });
  const delKeyPoint = (i) => updateContent({ keyPoints: (doc.content.keyPoints || []).filter((_, j) => j !== i) });

  if (loading) return <><Header /><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div></>;
  if (!doc) return null;

  return (
    <>
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/library')}><ArrowLeft className="w-4 h-4 mr-1" /> Back to Library</Button>
          <div className="flex gap-2">
            {doc.docType === 'policy-summary' && doc.data?.sourceText && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm"><FileText className="w-4 h-4 mr-1" /> Source Policy</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                  <DialogHeader><DialogTitle>Original Policy — {doc.data.sourceName || 'Source'}</DialogTitle></DialogHeader>
                  <div className="overflow-y-auto text-sm text-gray-700 whitespace-pre-wrap border rounded-lg p-4 bg-gray-50">{doc.data.sourceText}</div>
                </DialogContent>
              </Dialog>
            )}
            <Button variant="outline" size="sm" onClick={() => printPreview(previewRef.current, `print-size-${printSize}`)}><Printer className="w-4 h-4 mr-1" /> Print</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save</Button>
          </div>
        </div>
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div className="border rounded-xl bg-gray-100 p-4 overflow-hidden">
            <div ref={previewRef}><PreviewScaler><OnePagerPreview onePager={doc} /></PreviewScaler></div>
          </div>
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <div><Label className="text-xs">Title</Label><Input value={doc.content?.title || doc.title || ''} onChange={(e) => { update({ title: e.target.value }); updateContent({ title: e.target.value }); }} /></div>
              <div><Label className="text-xs">Subtitle</Label><Input value={doc.content?.subtitle || ''} onChange={(e) => updateContent({ subtitle: e.target.value })} /></div>
              {doc.content?.introduction !== undefined && <div><Label className="text-xs">Introduction</Label><Textarea rows={3} value={doc.content?.introduction || ''} onChange={(e) => updateContent({ introduction: e.target.value })} /></div>}
              <div><Label className="text-xs">Facility Name</Label><Input value={doc.facilityName || ''} onChange={(e) => update({ facilityName: e.target.value })} /></div>
            </CardContent></Card>
            {doc.content?.keyPoints && (
              <Card><CardContent className="p-4">
                <div className="flex items-center justify-between mb-2"><Label className="text-xs">Key Points</Label><Button size="sm" variant="ghost" onClick={addKeyPoint}><Plus className="w-3 h-3" /></Button></div>
                {(doc.content.keyPoints || []).map((kp, i) => (
                  <div key={i} className="flex gap-1 mb-2"><Input value={kp} onChange={(e) => setKeyPoint(i, e.target.value)} className="text-xs" /><Button size="icon" variant="ghost" className="text-red-500" onClick={() => delKeyPoint(i)}><Trash2 className="w-3 h-3" /></Button></div>
                ))}
              </CardContent></Card>
            )}
            <Card><CardContent className="p-4">
              <Label className="text-xs flex items-center gap-1 mb-2"><Palette className="w-3 h-3" /> Color Scheme</Label>
              <div className="grid grid-cols-4 gap-2">{colorSchemes.map((c) => <button key={c.name} onClick={() => update({ colorScheme: c })} className={`h-9 rounded-lg border-2 ${doc.colorScheme?.name === c.name ? 'border-gray-900' : 'border-transparent'}`} style={{ backgroundColor: c.primary }} title={c.name} />)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <Label className="text-xs mb-2 block">Print Size</Label>
              <select value={printSize} onChange={(e) => setPrintSize(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">{printSizes.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.description}</option>)}</select>
            </CardContent></Card>
          </div>
        </div>
      </div>
    </>
  );
}
